import {faHourglassHalf, faGift, faArrowUpRightFromSquare} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {exhaustive} from '@freckle/exhaustive'
import {maybe} from '@freckle/maybe'
import type {NonEmptyArray} from '@freckle/non-empty'
import {mkNonEmpty, nonEmptyToArray} from '@freckle/non-empty'
import {PATHS} from '@freckle/educator-entities/ts/common/helpers/paths'
import {Flex} from '@freckle/educator-materials/ts/components/flex'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {cellFromRow, Table} from '@freckle/educator-materials/ts/components/table'
import {H2, Text} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import type {ReferralAttrs} from '../models/referral'
import {
  referralListContainer,
  statusWaiting,
  statusEarned,
  statusParagraph,
  contactUs,
  referralContainer,
} from './referral-program.module.scss'

type Props = {referrals: Array<ReferralAttrs>}

const EmptyReferralList = () => {
  const {t} = useSafeTranslation()
  return (
    <Flex flexDirection="column" justifyContent="center" alignItems="center">
      <img src={`${PATHS.imageAssetsUrl}/teacher-dashboard/referral-program/class.svg`} />
      <Text style="caption-2">{t('REFERRAL_PROGRAM_REFERRALS_EMPTY')}</Text>
    </Flex>
  )
}

type StatusT = 'awaiting_student_use' | 'earned_referral'

type ReferrallStatusIconProps = {status: StatusT}

const ReferrallStatusIcon = (props: ReferrallStatusIconProps) => {
  const {status} = props

  switch (status) {
    case 'earned_referral':
      return <FontAwesomeIcon icon={faGift} />
    case 'awaiting_student_use':
      return <FontAwesomeIcon icon={faHourglassHalf} />
    default:
      exhaustive(status)
  }
}

type ReferralStatusProps = {status: StatusT}

const getStatusClassName = (status: StatusT): string => {
  switch (status) {
    case 'earned_referral':
      return statusEarned
    case 'awaiting_student_use':
      return statusWaiting
    default:
      exhaustive(status)
  }
}

const ReferralStatus = (props: ReferralStatusProps) => {
  const {status} = props
  const {t} = useSafeTranslation()

  return (
    <div className={getStatusClassName(status)}>
      <ReferrallStatusIcon status={status} />
      <p className={statusParagraph}>{t('REFERRAL_PROGRAM_REFERRALS_TABLE_STATUS', {status})}</p>
    </div>
  )
}

const NonEmptyReferralList = (props: {referrals: NonEmptyArray<ReferralAttrs>}) => {
  const {referrals} = props
  const {t} = useSafeTranslation()

  return (
    <Table
      data={nonEmptyToArray(referrals)}
      columns={[
        {
          Header: t('REFERRAL_PROGRAM_REFERRALS_TABLE_TEACHER_HEADER'),
          accessor: v => v.referredTeacherName,
          Cell: cellFromRow(referral => <Text style="body-2">{referral.referredTeacherName}</Text>),
          id: 'referredTeacherName',
          alignment: 'left',
        },
        {
          Header: t('REFERRAL_PROGRAM_REFERRALS_TABLE_STATUS_HEADER'),
          accessor: v => v.status,
          Cell: cellFromRow(referral => <ReferralStatus status={referral.status} />),
          id: 'status',
          alignment: 'left',
        },
      ]}
    />
  )
}

type ReferralListContainerProps = {children: React.ReactNode}

const ReferralListContainer = (props: ReferralListContainerProps) => {
  const {children} = props
  return <div className={referralListContainer}>{children}</div>
}

// Main Component
const ReferredInformation = (props: Props): React.ReactElement => {
  const {referrals} = props
  const {t} = useSafeTranslation()
  const mReferrals = mkNonEmpty(referrals)

  return (
    <Flex flexDirection="column" alignItems="center">
      <H2>{t('REFERRAL_PROGRAM_REFERRALS_SUBTITLE')}</H2>
      <div className={referralContainer}>
        <Flex className={contactUs} flexDirection="row" justifyContent="flex-end">
          <Text style="body-3">
            <SafeTrans
              i18nKey="REFERRAL_QUESTIONS_LINK_CONTACT_US"
              components={{
                emailLink: (
                  <Link
                    target="_blank"
                    rel="noopener"
                    href="mailto:team@freckle.com"
                    reactRouter={false}
                    leftIcon={faArrowUpRightFromSquare}
                  />
                ),
              }}
            />
          </Text>
        </Flex>
        <ReferralListContainer>
          {maybe(
            () => (
              <EmptyReferralList />
            ),
            r => (
              <NonEmptyReferralList referrals={r} />
            ),
            mReferrals,
          )}
        </ReferralListContainer>
      </div>
    </Flex>
  )
}

export {ReferredInformation}
